"use client";

import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "#/ui/form";
import { Input } from "#/ui/standard-input";
import { ComposedSelect } from "#/ui/select";
import MultiSelect from "#/ui/multi-select";
import { Checkbox } from "#/ui/checkbox";
import { PhoneInput } from "#/ui/phone-input";
import { ChevronDown } from "lucide-react";
import { cn } from "#/lib/utils";

interface BaseFieldProps {
  name: string;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  rightHeader?: React.ReactNode;
}

export const TextField = ({
  name,
  label,
  description,
  type,
  rightHeader,
  testId,
  ...props
}: BaseFieldProps &
  Omit<React.ComponentProps<typeof Input>, "type"> & {
    type?: "text" | "password" | "email" | "number" | "tel" | "url";
    testId?: string;
  }) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const registerProps: any = {
          value: field.value ?? "",
          onChange: field.onChange,
          onBlur: field.onBlur,
          name: field.name,
        };

        // Pass type through register if it's not password (password uses secure prop)
        if (type && type !== "password") {
          registerProps.type = type;
        }

        return (
          <FormItem className={cn("relative", props.className)}>
            <FormControl>
              <Input
                title={label}
                secure={type === "password"}
                rightHeader={rightHeader}
                register={registerProps}
                id={testId}
                {...props}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const SelectField = ({
  name,
  label,
  description,
  options,
  className,
  titleClassName,
  ...props
}: BaseFieldProps & { options: { label: string; value: string }[] } & Omit<
    React.ComponentProps<typeof ComposedSelect>,
    "data" | "onChange" | "value"
  >) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormControl>
            <ComposedSelect
              title={label}
              data={options}
              value={field.value}
              onChange={field.onChange}
              icon={ChevronDown}
              className={cn("rounded-lg min-h-10 h-10 border", className)}
              titleClassName={cn(
                "text-[11px] left-[0.625rem] top-[-7px] px-1 bg-card",
                titleClassName,
              )}
              {...props}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const MultiSelectField = ({
  name,
  label,
  description,
  options,
  ...props
}: BaseFieldProps & { options: any[] } & Omit<
    React.ComponentProps<typeof MultiSelect>,
    "options" | "defaultValues" | "onChange"
  >) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={props.className}>
          <FormControl>
            <MultiSelect
              title={label}
              options={options}
              defaultValues={field.value || []}
              onChange={(vals) => field.onChange(vals?.map((v) => v.value))}
              {...props}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const CheckboxField = ({
  name,
  label,
  description,
  ...props
}: BaseFieldProps & React.ComponentProps<typeof Checkbox>) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            "flex flex-row items-start space-x-3 space-y-0 p-4",
            props.className,
          )}
        >
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={props.disabled}
              {...props}
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            {label && <FormLabel>{label}</FormLabel>}
            {description && <FormDescription>{description}</FormDescription>}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const PhoneField = ({
  name,
  label,
  description,
  ...props
}: BaseFieldProps & React.ComponentProps<typeof PhoneInput>) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={props.className}>
          <FormControl>
            <PhoneInput
              title={label}
              {...field}
              {...props}
              onChange={(value) => field.onChange(value)}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

// Generic field for custom components that need form integration
export const CustomField = <T,>({
  name,
  description,
  className,
  render,
}: {
  name: string;
  description?: string;
  className?: string;
  render: (props: {
    value: T;
    onChange: (value: T) => void;
  }) => React.ReactNode;
}) => {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormControl>
            {render({
              value: field.value,
              onChange: field.onChange,
            })}
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
